import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import useAuth from '../../Hooks/useAuth';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    Alert as ServaAlert,
    Button,
    Col,
    Divider,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { SimplifiedUser } from '../Shared/SimplifiedUser';
import Report from '../../Interfaces/Responses/Dashboard/Report';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';
import AutoRunTargetEmail from '../../Interfaces/Responses/Schedules/AutoRunTargetEmail';

type AutomaticDeliveryTargetEmailProps = {
    report: Report | undefined;
    autoRunTargetEmail: AutoRunTargetEmail | undefined;
    handleSetADAutoRunTargetEmail: (
        autoRunTargetEmail: AutoRunTargetEmail
    ) => void;
    close: () => void;
};

const AutomaticDeliveryTargetEmail: React.FC<AutomaticDeliveryTargetEmailProps> = ({ close, ...props }) => {

    const { user } = useAuth();
    const [formAutomaticDeliveryTargetEmail] = Form.useForm();

    const [users, setUsers] = useState<SimplifiedUser[]>([]);
    
    const [adReportID, setADReportID] = useState<number | undefined>();
    const [adReportName, setADReportName] = useState<string | undefined>();
    const [adReportGroupName, setADReportGroupName] = useState<string | undefined>();

    const [adUserID, setADUserID] = useState<number | undefined>();
    const [adFileFormat, setADFileFormat] = useState<number | undefined>();
    const [adTargetEmail, setADTargetEmail] = useState<string | undefined>();

    const [currentError, setCurrentError] = useState('');

    useEffect(() => {

        setADReportID(((props.report?.id) && (props.report?.id > 0)) ? props.report.id : 0);
        setADReportName(((props.report?.name) && (props.report?.name !== '')) ? props.report.name : '');
        setADReportGroupName(((props.report?.groupName) && (props.report?.groupName !== '')) ? props.report.groupName : '');

        getUsers(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { mutate: getUsers, isLoading: isGetUsersLoading, isError: isGetUsersError, error: getUsersError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: showDisabled, };
                const responseData = AxiosUtil.get<GetUserAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ', err);
            },
            onSuccess: (response: GetUserAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getUsers() response ****');
                // console.log(response);
                // console.log(props.autoRunTargetEmail);
                // ---- END DEBUG

                if (response) {
                    let simplifiedUsers: SimplifiedUser[] = response?.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                firstName: eachElement.firstName,
                                lastName: eachElement.lastName,
                                email: eachElement.email,
                                password: eachElement.password,
                                userName: eachElement.userName,
                                phone: eachElement.phone,
                                adminComment: eachElement.adminComment,
                                changePassword: eachElement.changePasswordRequired,
                                reportsOnly: eachElement.reportsOnly,
                                isSystemAccount: eachElement.isSystemAccount,
                                isDemo: eachElement.isDemo,
                                securityAdmin: eachElement.securityAdmin,
                                techTeam: eachElement.techTeam,
                                deleted: eachElement.deleted,
                            };
                        }
                    ).sort((a, b) => a.email.localeCompare(b.email));
                    simplifiedUsers = simplifiedUsers.filter(
                        (eachUser) => !eachUser.deleted
                    );
                    setUsers(simplifiedUsers);
                }
            }
        }
    );

    const handleADUserIDUpdate = (e: string) => {
        setADUserID(parseInt(e));
    };

    const handleADFileFormatUpdate = (e: string) => {
        setADFileFormat(parseInt(e));
    };

    const handleADTargetEmailUpdate = () => {
        let newADTargetEmail = formAutomaticDeliveryTargetEmail.getFieldValue('adTargetEmail');

        setADTargetEmail(newADTargetEmail);
    };

    const onFinish = (values: any) => {
        // ---- DEBUG
        // console.log('ADTE');
        // console.log(values);
        // ---- ENDDEBUG

        const newAutoRunTargetEmail: AutoRunTargetEmail =
        {
            userID: user?.id ?? 0, //???? use input from popup ????
            targetEmail: (values.adTargetEmail as string) ?? '',
            fileFormat: (values.adFileFormat as number) ?? 0,
            id: props.autoRunTargetEmail?.id ?? 0,
            autoRunID: props.report?.autoRun.id ?? 0,
            autoRunScheduleID: props.autoRunTargetEmail?.autoRunScheduleID ?? 0,
            autoRunTargetType: props.autoRunTargetEmail?.autoRunTargetType ?? 0,
        };
        // ---- DEBUG
        // console.log(newAutoRunTargetEmail);
        // ---- END DEBUG


        props.handleSetADAutoRunTargetEmail(newAutoRunTargetEmail);

        close();
    }
    
    const determineWhichError = () => {
        if ( isGetUsersError ) {
            return currentError + ' ' + (getUsersError as any).response?.data.message;
        }
        else {
            return '';
        }
    } 

    return (
        <>
            <Spin spinning={
                            isGetUsersLoading ||
                            (currentError !== '')
                           }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetUsersError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type="error"
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '6px', color: 'white', height: '100%', padding: '4px', width: '100%'}} level={4}>Automatic Delivery Target Email Options</Title>
                    </Text>
                </Space>
                <Form
                    form={formAutomaticDeliveryTargetEmail}
                    initialValues = {{
                        adUserID: ((props.autoRunTargetEmail?.userID === undefined) || (props.autoRunTargetEmail?.userID === 0)) ? user?.runningUser.id : props.autoRunTargetEmail?.userID,
                        adFileFormat: (props.autoRunTargetEmail?.fileFormat ?? 2).toString(),
                        adTargetEmail: (props.autoRunTargetEmail?.targetEmail ?? ''),
                    }}
                    key='automaticDeliveryTargetEmail'
                    onFinish={(values: any) => onFinish(values)}
                    onFinishFailed={(error) => console.log(error)}
                >
                    <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                        <Text italic type='secondary'>{'[' + adReportID + '] ' + adReportGroupName + ': ' + adReportName}</Text>
                    </Space>
                    <Divider style={{padding: 0, margin: 8}}></Divider>
                    <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                        <Col span={13}>
                            <div style={{textAlign:'left'}}>
                                <Form.Item
                                    name='adUserID'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Select
                                        label='User ID:'
                                        value={adUserID}
                                        showSearch
                                        onChange={(e: any) => handleADUserIDUpdate(e)}
                                    >
                                        {users.map(
                                            (eachUser) => {
                                                return (
                                                    <Option
                                                        value={eachUser.id}
                                                        label={eachUser.firstName + ' ' + eachUser.lastName}
                                                    >
                                                        {eachUser.firstName + ' ' + eachUser.lastName + ' (' + eachUser.email + ')'}
                                                    </Option>
                                                );
                                            }
                                        )}
                                    </Select>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                        <Col span={10}>
                            <div style={{textAlign:'left'}}>
                                <Form.Item
                                    name='adFileFormat'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Select
                                        label='File Format:'
                                        value={adFileFormat}
                                        onChange={(e: any) => handleADFileFormatUpdate(e)}
                                    >
                                        <Option value="1">CSV</Option>
                                        <Option value="2">Excel</Option>
                                        <Option value="3">PDF</Option>
                                    </Select>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                        <Col span={24}>
                            <div style={{textAlign:'left', width:'100%'}}>
                                <label>Target Emails:</label>
                            </div>
                        </Col>
                    </Row>
                    <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                        <Col span={24}>
                            <div style={{textAlign:'left', width:'100%'}}>
                                <Form.Item
                                    name='adTargetEmail'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Input
                                        size='middle'
                                        value={adTargetEmail}
                                        onChange={() => handleADTargetEmailUpdate()}
                                    ></Input>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    <Row style={{ padding: '0px', margin: '0px', lineHeight: '50%' }}>
                        <Col span={24}>
                            <Divider style={{ padding: '0px', paddingBottom: '6px', margin: '0px' }}></Divider>
                        </Col>
                    </Row>
                    <Row style={{ padding: '8px', paddingTop: '8px', paddingBottom: '2px', lineHeight: '50%' }}>
                        <Col span={19}></Col>
                        <Col span={2} style={{display: 'flex', justifyContent: 'center'}}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name="adApplyButton"
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType="submit"
                                        type="primary"
                                    >
                                        Apply
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1}></Col>
                        <Col span={2} style={{display: 'flex', justifyContent: 'center'}}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name="adCancelButton"
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button type="default" onClick={() => close()}>
                                        Cancel
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AutomaticDeliveryTargetEmail;
